import FontBody1 from '../../../../../components/fonts/font-body1/font-body1';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../components/fonts/font-props';
import Card from '../../../../../components/layouts/card/card';
import customizationStore from '../../../../../stores/customizations/customization-store';
import { FormField } from '../../../../../components/form-field/form-field';
import {
	ACTIONS_VIEW,
	ACTIONS_VIEW_MAP,
	actionsViewList,
	optionLayoutList,
	OPTIONS_LAYOUT_MAP,
	POLICY_RULE_DISPLAY,
	POLICY_RULE_DISPLAY_MAP,
	policyRuleDisplayList,
	S2E_LAYOUT_TYPE, S2E_LAYOUT_TYPE_MAP,
	VARIANTS_VIEW,
	VARIANTS_VIEW_MAP,
	variantsViewList
} from '../../../../../models/User';
import ControlGroupWithPreview
	from '../../../../../components/layouts/control-group-with-preview/control-group-with-preview';
import ListLayout from '../../../../../components/layouts/list-layout/list-layout';
import { observer } from 'mobx-react';
import { Toggle } from '../../../../../components/inputs/toggle/toggle';
import { IOption, SelectDropdown } from '../../../../../components/inputs/select-dropdown/select-dropdown';
import { SingleValue } from 'react-select';
import OptionSelectionVertical from '../../../../../images/previews/option-selection-vertical.svg';
import OptionSelectionHorizontal from '../../../../../images/previews/option-selection-horizontal.svg';
import PolicyRuleDisplayResolution from '../../../../../images/previews/policy-rule-display/resolution.svg';
import PolicyRuleDisplayReturnMethod from '../../../../../images/previews/policy-rule-display/return-method.svg';
import { FormFieldLine } from '../../../../../components/form-field-line/form-field-line';
import currentPortalStore from '../../../../../stores/current-portal-store';
import { FEATURES } from '../../../../../models/Plan';
import Hr from '../../../../../components/hr/hr';
import S2ELayoutPreview from './s2e-layout-preview/s2-e-layout-preview';
import S2ELayoutSelect from './s2e-layout-preview/layout-select/s2e-layout-select';
import LayoutItemFormGroup from './s2e-layout-preview/layout-item-form-group/layout-item-form-group';
import { isSet } from '../../../../../models/model-types-methods';

interface IProps {
	onExpandClick: (expanded: boolean) => void;
	isExpanded: boolean;
}

const GeneralCard: React.FC<IProps> = observer(({ isExpanded, onExpandClick }) => {

	const isBxGyFeatureEnabled = currentPortalStore.isFeatureAllowed(FEATURES.BXGY);
	const isA2ACartFeatureEnabled = currentPortalStore.isFeatureAllowed(FEATURES.ANY_TO_ANY_EXCHANGES_FEATURE);
	const isS2EFeatureEnabled = currentPortalStore.isFeatureAllowed(FEATURES.SWITCH_TO_EXCHANGE);
	const activeCustomizationStore = customizationStore.getSelectedStore();
	const classicCustomizationStore = customizationStore.getClassicStore();

	const selectedVariantView = VARIANTS_VIEW_MAP[classicCustomizationStore.variantSelection];
	const selectedActionView = ACTIONS_VIEW_MAP[classicCustomizationStore.actionSelection];

	const balanceOrderOptions: IOption[] = [
			{ value: 0, label: 'Refund first, then payment' },
			{ value: 1, label: 'Payment first, then refund' },
		];

	const selectedBalanceOrder = classicCustomizationStore.showNewItemsFirst ? balanceOrderOptions[1] : balanceOrderOptions[0];

	const optionVal = classicCustomizationStore.displayVertical ? 'true' : 'false';
	const selectedOptionsLayout = OPTIONS_LAYOUT_MAP[optionVal];

	const selectedPolicyRuleDisplay = POLICY_RULE_DISPLAY_MAP[classicCustomizationStore.policyRuleDisplay];

	const optionSelectionPreview = () => {
		if (classicCustomizationStore.displayVertical) {
			return <img src={OptionSelectionVertical} alt="" />;
		}
		return <img src={OptionSelectionHorizontal} alt="" />;
	};

	const policyRuleDisplayPreview = () => {
		if (classicCustomizationStore.policyRuleDisplay === POLICY_RULE_DISPLAY.RESOLUTION) {
			return <img src={PolicyRuleDisplayResolution} alt="" />;
		} else {
			return <img src={PolicyRuleDisplayReturnMethod} alt=""/>;
		}
	};

	const selectedS2ELayoutType = activeCustomizationStore.switchToExchangeLayoutType ?? S2E_LAYOUT_TYPE.ONE_RECTANGLE;

	return (
		<Card
			toolbar={{
				title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>General</FontBody1>
			}}
			isExpanded={isExpanded}
			expandCallback={onExpandClick}
		>
			<FormField
				label='Return Portal'
				instructions='Change the look and feel of your return portal.'
			>
			</FormField>
			<ControlGroupWithPreview hasTopBorder={false}>
				<ListLayout>
					<FormFieldLine
						label='Display SKU for product items'
                        note="Enable to display each item's SKU (when available) in the portal."
					>
						<Toggle
							label=""
							checked={classicCustomizationStore.skuDisplay}
							onChange={classicCustomizationStore.setSkuDisplay}
						/>
					</FormFieldLine>
					<FormFieldLine
						label='Display descriptions for item actions'
                        note='Enable to add a short, customizable description to each action option (return/exchange/etc) in the portal.'
					>
						<Toggle
							label=""
							checked={classicCustomizationStore.showActionsDescriptions}
							onChange={classicCustomizationStore.setShowActionsDescriptions}
						/>
					</FormFieldLine>
					<FormField
						label='Portal Balance Display Order'
						instructions='Choose how the balance is displayed to your customers, whether to first display the amount they need to pay or the amount they will be refunded.'
					>
						<SelectDropdown
							value={selectedBalanceOrder}
							onChange={(v: SingleValue<IOption>) => {
								classicCustomizationStore.setShowNewItemsFirst(Boolean(v?.value));
							}}
							options={balanceOrderOptions}
						/>
					</FormField>
					<FormField
						label='Variant Selection Screen'
						instructions='Define how customers will choose a new variant.'
					>
						<SelectDropdown
							value={selectedVariantView}
							onChange={(v: SingleValue<IOption>) => {
								classicCustomizationStore.setVariantSelection(v?.value as VARIANTS_VIEW);
							}}
							options={variantsViewList()}
						/>
					</FormField>
				</ListLayout>
			</ControlGroupWithPreview>

			<FormField
				label='Order & RMAs Tabs'
				instructions='Tabs that appear when the order has one or more RMAs.'
			>
			</FormField>
			<ControlGroupWithPreview
				hasTopBorder={false}
				preview={<div className="preview"></div>}
			>
				<FormField
					label='Item Selection Display'
					instructions="Define whether customers select an action directly from the item selection screen or on a separate page."
				>
					<SelectDropdown
						value={selectedActionView}
						onChange={(v: SingleValue<IOption>) => {
							classicCustomizationStore.setActionSelection(v?.value as ACTIONS_VIEW);
						}}
						options={actionsViewList()}
						disabled={isBxGyFeatureEnabled}
					/>
				</FormField>
			</ControlGroupWithPreview>

			<ControlGroupWithPreview
				hasTopBorder={false}
				preview={<div className="preview">{optionSelectionPreview()}</div>}
			>
				<FormField
					label='Option Selection Display'
					instructions="Define the order in which options that are displayed in columns should be arranged."
				>
					<SelectDropdown
						value={selectedOptionsLayout}
						onChange={(v: SingleValue<IOption>) => {
							classicCustomizationStore.setDisplaVertical(v?.value === 'true');
						}}
						options={optionLayoutList()}
					/>
				</FormField>
			</ControlGroupWithPreview>

			<Hr />

			{(isA2ACartFeatureEnabled && isS2EFeatureEnabled && isSet(activeCustomizationStore.switchToExchangeLayoutType)) && (
				<>
					<FormField label='Switch to Exchange page' children={null}/>

					<ControlGroupWithPreview
						hasTopBorder={false}
						preview={<div className="preview">
							<S2ELayoutPreview type={activeCustomizationStore.switchToExchangeLayoutType!} images={activeCustomizationStore.switchToExchangeLayoutImages}/>
						</div>}
					>
						<ListLayout>
							<S2ELayoutSelect/>

							{Array.from({ length: S2E_LAYOUT_TYPE_MAP[selectedS2ELayoutType].numberOfImages }, (_, i) => i + 1).map((_, index) => (
								<LayoutItemFormGroup key={index} index={index} />
							))}
						</ListLayout>
					</ControlGroupWithPreview>

					<Hr/>
				</>
			)}

			<FormField label='Select Resolution Page' children={null}/>

			<ControlGroupWithPreview
				hasTopBorder={false}
				preview={<div className="preview">{policyRuleDisplayPreview()}</div>}
			>
				<FormField
					label='Policy Rule Display'
					instructions="Define the display priority for return methods and resolutions when customers select policy rules in the portal."
				>
					<SelectDropdown
						value={selectedPolicyRuleDisplay}
						onChange={(v: SingleValue<IOption>) => {
							classicCustomizationStore.setPolicyRuleDisplay(v?.value as POLICY_RULE_DISPLAY);
						}}
						options={policyRuleDisplayList()}
					/>
				</FormField>

			</ControlGroupWithPreview>
			<ControlGroupWithPreview>
				<FormFieldLine
					label='Automatically select the same policy for the remaining items'
					note="When enabled, the same policy as the first selected item will be applied to other items, if available"
				>
					<Toggle
						label=""
						checked={currentPortalStore.setting?.autoSelectSamePolicy}
						onChange={currentPortalStore.setting?.setAutoSelectSamePolicy}
					/>
				</FormFieldLine>
			</ControlGroupWithPreview>
		</Card>

	)
})

export default GeneralCard
